// Акции: счастливые часы и N+1. Правила уезжают на кассу с каталогом —
// офлайн-чек считает их так же, как онлайн.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminPromos, type PromoRule } from '@dastarhan/contracts';
import { z } from 'zod';

const API = '/api/v1';
const DAYS: { i: number; label: string }[] = [
  { i: 1, label: 'Пн' }, { i: 2, label: 'Вт' }, { i: 3, label: 'Ср' },
  { i: 4, label: 'Чт' }, { i: 5, label: 'Пт' }, { i: 6, label: 'Сб' }, { i: 0, label: 'Вс' },
];
const AdminMenuLite = z.object({
  products: z.array(z.object({ id: z.string(), name: z.string(), isActive: z.boolean() })),
});

const toMin = (hhmm: string) => {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  return h * 60 + m;
};
const toHHMM = (min: number) =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

function ruleText(rule: PromoRule, productName: (id: string) => string): string {
  if (rule.type === 'HAPPY_HOURS') {
    // Дни могли не прийти у правила, заведённого до появления поля
    const on = rule.days ?? [];
    const days = on.length === 0 ? 'каждый день'
      : DAYS.filter((d) => on.includes(d.i)).map((d) => d.label).join(', ');
    return `−${rule.percent}% на чек · ${days} · ${toHHMM(rule.fromMin)}–${toHHMM(rule.toMin)}`;
  }
  return `${productName(rule.productId)}: каждые ${rule.n + 1} — одна бесплатно`;
}

export function PromoScreen({ token }: { token: string }) {
  const qc = useQueryClient();
  const h = { Authorization: `Bearer ${token}` };
  const jh = { ...h, 'Content-Type': 'application/json' };
  const refresh = () => void qc.invalidateQueries({ queryKey: ['promos'] });

  const promos = useQuery({
    queryKey: ['promos'],
    queryFn: async () => AdminPromos.parse(await (await fetch(`${API}/menu/promos`, { headers: h })).json()),
  });
  const menu = useQuery({
    queryKey: ['admin-menu-lite'],
    queryFn: async () => AdminMenuLite.parse(await (await fetch(`${API}/menu/admin`, { headers: h })).json()),
  });

  const create = useMutation({
    mutationFn: (b: { name: string; rule: PromoRule }) =>
      fetch(`${API}/menu/promos`, { method: 'POST', headers: jh, body: JSON.stringify(b) }),
    onSuccess: refresh,
  });
  const patch = useMutation({
    mutationFn: ({ id, ...b }: { id: string; isActive?: boolean; name?: string }) =>
      fetch(`${API}/menu/promos/${id}`, { method: 'PATCH', headers: jh, body: JSON.stringify(b) }),
    onSuccess: refresh,
  });

  const [kind, setKind] = useState<'HAPPY_HOURS' | 'N_PLUS_ONE'>('HAPPY_HOURS');
  const [name, setName] = useState('');
  const [percent, setPercent] = useState('20');
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [from, setFrom] = useState('15:00');
  const [to, setTo] = useState('17:00');
  const [productId, setProductId] = useState('');
  const [n, setN] = useState('2');

  if (promos.isPending || menu.isPending) return <p className="empty">Загружаем…</p>;
  if (promos.isError || menu.isError || !promos.data || !menu.data) {
    return <p className="empty">Не удалось загрузить. Обновите страницу.</p>;
  }

  const products = menu.data.products.filter((p) => p.isActive);
  const productName = (id: string) => products.find((p) => p.id === id)?.name ?? '—';

  const submit = () => {
    if (!name.trim()) return;
    const rule: PromoRule | null =
      kind === 'HAPPY_HOURS'
        ? (days.length && toMin(to) > toMin(from)
            ? { type: 'HAPPY_HOURS', percent: Math.min(100, Math.max(1, Number(percent) || 0)),
                days, fromMin: toMin(from), toMin: toMin(to) }
            : null)
        : (productId
            ? { type: 'N_PLUS_ONE', productId, n: Math.max(1, Math.trunc(Number(n)) || 0) }
            : null);
    if (!rule) return;
    create.mutate({ name: name.trim(), rule });
    setName('');
  };

  return (
    <div className="promo-edit">
      <section className="menu-cat">
        <header><h2>Новая акция</h2></header>
        {/* Ограничение стоит назвать ДО создания, а не после жалобы */}
        <p className="hint">
          Акция действует на всех точках сети — как и меню с ценами.
          Чтобы скидка работала только в одном зале, сделайте её
          вручную на кассе той точки.
        </p>
        <div className="promo-form">
          <div className="row">
            <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
              <option value="HAPPY_HOURS">Счастливые часы (% на чек)</option>
              <option value="N_PLUS_ONE">N+1 (товар бесплатно)</option>
            </select>
            <input placeholder="Название: «Обеденные −20%»…" value={name}
              onChange={(e) => setName(e.target.value)} />
          </div>

          {kind === 'HAPPY_HOURS' ? (
            <div className="row wrap">
              <label>Скидка, %
                <input inputMode="numeric" value={percent} style={{ width: 70 }}
                  onChange={(e) => setPercent(e.target.value)} />
              </label>
              <div className="day-chips" role="group" aria-label="Дни недели">
                {DAYS.map((d) => (
                  <button key={d.i} type="button"
                    className={`chip ${days.includes(d.i) ? 'chip-on' : ''}`}
                    onClick={() => setDays((cur) =>
                      cur.includes(d.i) ? cur.filter((x) => x !== d.i) : [...cur, d.i])}>
                    {d.label}
                  </button>
                ))}
              </div>
              <label>С <input type="time" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
              <label>До <input type="time" value={to} onChange={(e) => setTo(e.target.value)} /></label>
            </div>
          ) : (
            <div className="row wrap">
              <label>Товар
                <select value={productId} onChange={(e) => setProductId(e.target.value)}>
                  <option value="">— выберите —</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <label>Купи N
                <input inputMode="numeric" value={n} style={{ width: 60 }}
                  onChange={(e) => setN(e.target.value)} />
              </label>
              <span className="hint-inline">каждые N+1 единиц — одна бесплатно</span>
            </div>
          )}

          <button className="btn accent" style={{ alignSelf: 'flex-start' }}
            disabled={!name.trim()} onClick={submit}>Создать акцию</button>
        </div>
      </section>

      {promos.data.length === 0 && (
        <p className="empty">Акция считается прямо на кассе — даже офлайн. Начните со «счастливых часов» на тихое время.</p>
      )}

      {promos.data.map((p) => (
        <div key={p.id} className={`menu-row promo-row-admin ${p.isActive ? '' : 'archived'}`}>
          <span className="name"><b>{p.name}</b><small>{ruleText(p.rule, productName)}</small></span>
          <button className="btn small" onClick={() => patch.mutate({ id: p.id, isActive: !p.isActive })}>
            {p.isActive ? 'Остановить' : 'Запустить'}
          </button>
        </div>
      ))}
    </div>
  );
}
